'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Tooltip, Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../../contexts/translation.context';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { RevshareOffer } from '../types';
import BlockedCountriesTooltip from './BlockedCountriesTooltip';
import { SYSTEM_FEE_PERCENT } from '../constants';
import {
  OnlyFansIc,
  InstagramIc,
  TwitterIc,
  GoogleIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  IcAnotherSoc,
} from '../../../../components/icons';

// Get source icon for blocked sources
const getSourceIcon = (source: string, size: number = 16) => {
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    onlyfans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    reddit: <IcReddit size={size} />,
    instagram: <InstagramIc size={size} fill="#A8A8B0" />,
    tiktok: <IcTikTok size={size} />,
    google: <GoogleIc size={size} fill="#A8A8B0" />,
    telegram: <IcTg size={size} />,
    twitter: <TwitterIc size={size} fill="#A8A8B0" />,
    youtube: <IcYoutube size={size} />,
    facebook: <Icon icon="ic:baseline-facebook" width={size} className="text-neutral-8" />,
    tinder: <Icon icon="simple-icons:tinder" width={size} className="text-neutral-8" />,
  };

  return iconMap[source?.toLowerCase()] || <IcAnotherSoc size={size} />;
};

// Get source label
const getSourceLabel = (source: string) => {
  const labelMap: Record<string, string> = {
    only_fans: 'OnlyFans',
    onlyfans: 'OnlyFans',
    reddit: 'Reddit',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    telegram: 'Telegram',
    twitter: 'Twitter',
    youtube: 'YouTube',
    facebook: 'Facebook',
    tinder: 'Tinder',
  };

  return labelMap[source?.toLowerCase()] || source;
};

interface OfferDetailsCellProps {
  offer: RevshareOffer;
}

const OfferDetailsCell: React.FC<OfferDetailsCellProps> = ({ offer }) => {
  const { t } = useTranslations();

  // Get blocked countries
  const blockedCountries = offer.of_data?.blocked_countries || [];
  const blockedCountriesCount = blockedCountries.length;

  // Get blocked sources (prohibited_sources)
  const blockedSources = offer.offer?.prohibited_sources || [];
  const blockedSourcesCount = blockedSources.length;

  // Hover state for blocked sources tooltip
  const [isBlockedSourcesOpen, setIsBlockedSourcesOpen] = useState(false);
  const sourcesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hover state for blocked countries tooltip
  const [isBlockedCountriesOpen, setIsBlockedCountriesOpen] = useState(false);
  const countriesCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (sourcesCloseTimeoutRef.current) {
        clearTimeout(sourcesCloseTimeoutRef.current);
      }
      if (countriesCloseTimeoutRef.current) {
        clearTimeout(countriesCloseTimeoutRef.current);
      }
    };
  }, []);

  // Blocked sources hover handlers
  const openBlockedSources = () => {
    if (sourcesCloseTimeoutRef.current) {
      clearTimeout(sourcesCloseTimeoutRef.current);
      sourcesCloseTimeoutRef.current = null;
    }
    setIsBlockedSourcesOpen(true);
  };

  const scheduleBlockedSourcesClose = () => {
    if (sourcesCloseTimeoutRef.current) {
      clearTimeout(sourcesCloseTimeoutRef.current);
    }
    sourcesCloseTimeoutRef.current = setTimeout(() => {
      setIsBlockedSourcesOpen(false);
    }, 120);
  };

  // Blocked countries hover handlers
  const openBlockedCountries = () => {
    if (countriesCloseTimeoutRef.current) {
      clearTimeout(countriesCloseTimeoutRef.current);
      countriesCloseTimeoutRef.current = null;
    }
    setIsBlockedCountriesOpen(true);
  };

  const scheduleBlockedCountriesClose = () => {
    if (countriesCloseTimeoutRef.current) {
      clearTimeout(countriesCloseTimeoutRef.current);
    }
    countriesCloseTimeoutRef.current = setTimeout(() => {
      setIsBlockedCountriesOpen(false);
    }, 120);
  };

  // Get working hours text
  const getWorkingHoursText = () => {
    const chatProcessing = offer.offer?.chat_processing;
    if (!chatProcessing) return null;

    switch (chatProcessing) {
      case 'all time':
        return t('partner.revshare.offers.offerCard.allTimeChats') || '24/7 чаты';
      case 'custom work time':
        return t('partner.revshare.offers.offerCard.customWorkTime') || 'По расписанию';
      case 'AI':
        return t('partner.revshare.offers.offerCard.aiChats') || 'AI чаты';
      default:
        return null;
    }
  };

  const workingHours = getWorkingHoursText();

  // Show Free-trial label when free_trial_access is true
  const isFreeTrial = offer.offer?.free_trial_access === true;

  // Generate offer short ID and profile URL for tooltip
  const fullId = String(offer.of_user_id || '');
  const shortId = fullId.length > 4 ? fullId.slice(0, 4) : fullId;
  const offerShortId = `ON${shortId}`;
  const profileUrl = `https://onlyfans.com/${offer.of_user_name}`;

  return (
    <div className="flex flex-col gap-1">
      {/* Labels row */}
      <div className="flex items-center gap-2">
        <ResponsiveTooltip
          content={
            t('partner.revshare.offers.offerCard.byLinkTooltip') ||
            'Все пользователи, зарегистрированные по ссылке кампании, привязываются к вам'
          }
        >
          <Typography
            variant="body3"
            className="text-auth-ink text-xs font-semibold cursor-pointer"
          >
            {t('partner.revshare.offers.offerCard.byLink') || 'По ссылке'}
          </Typography>
        </ResponsiveTooltip>
        {isFreeTrial && (
          <ResponsiveTooltip
            content={
              t('partner.revshare.offers.offerCard.freeTrialTooltip') ||
              'Все пользователи, зарегистрированные по ссылке бесплатной пробной версии, привязываются к вам'
            }
          >
            <Typography
              variant="body3"
              className="text-auth-ink text-xs !font-semibold cursor-pointer"
            >
              Free-trial
            </Typography>
          </ResponsiveTooltip>
        )}
      </div>

      {/* Commission */}
      <div className="flex items-center gap-1">
        <Typography variant="body3" className="text-auth-muted text-xs">
          {t('partner.revshare.offers.offerCard.commission') || 'Комиссия'}:
        </Typography>
        <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
          {Math.max((offer.offer?.commission || 0) - SYSTEM_FEE_PERCENT, 0)}%
        </Typography>
        <ResponsiveTooltip
          content={
            t('partner.revshare.offers.offerCard.commissionInfo') ||
            'Сколько процентов вы получите от транзакций ваших поклонников NET'
          }
        >
          <Icon
            icon="solar:info-circle-bold"
            width={14}
            className="text-neutral-500 cursor-pointer"
          />
        </ResponsiveTooltip>
      </div>

      {/* Blocked resources */}
      {blockedSourcesCount > 0 ? (
        <Popover
          placement="bottom-start"
          offset={8}
          showArrow={false}
          isOpen={isBlockedSourcesOpen}
          onOpenChange={setIsBlockedSourcesOpen}
          shouldCloseOnBlur
          isDismissable
        >
          <PopoverTrigger>
            <div
              className="cursor-pointer flex items-center gap-1"
              onMouseEnter={openBlockedSources}
              onMouseLeave={scheduleBlockedSourcesClose}
            >
              <Typography variant="body3" className="text-auth-muted text-xs">
                {t('partner.revshare.offers.offerCard.blockedResources') ||
                  'Заблокированные ресурсы'}
                :
              </Typography>
              <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
                {blockedSourcesCount}
              </Typography>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-white border border-light rounded-3 shadow-[0px_0px_11px_rgba(0,0,0,0.1)]"
            onMouseEnter={openBlockedSources}
            onMouseLeave={scheduleBlockedSourcesClose}
          >
            <div className="flex flex-col pb-4">
              <div className="px-4 pt-4">
                <Typography variant="body2" className="text-auth-ink text-sm font-normal">
                  {t('partner.revshare.offers.blockedSourcesTooltip.title') ||
                    'Заблокированные источники'}
                </Typography>
              </div>
              <div className="flex flex-col px-4">
                {blockedSources.map((source) => (
                  <div key={source} className="flex items-center gap-2 h-8">
                    {getSourceIcon(source)}
                    <Typography variant="body2" className="text-auth-ink text-sm">
                      {getSourceLabel(source)}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-1">
          <Typography variant="body3" className="text-auth-muted text-xs">
            {t('partner.revshare.offers.offerCard.blockedResources') || 'Заблокированные ресурсы'}:
          </Typography>
          <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
            {blockedSourcesCount}
          </Typography>
        </div>
      )}

      {/* Blocked countries */}
      {blockedCountriesCount > 0 ? (
        <Popover
          placement="bottom-start"
          offset={12}
          showArrow
          isOpen={isBlockedCountriesOpen}
          onOpenChange={setIsBlockedCountriesOpen}
          shouldCloseOnBlur
          isDismissable
        >
          <PopoverTrigger>
            <div
              className="cursor-pointer flex items-center gap-1"
              onMouseEnter={openBlockedCountries}
              onMouseLeave={scheduleBlockedCountriesClose}
            >
              <Typography variant="body3" className="text-auth-muted text-xs">
                {t('partner.revshare.offers.offerCard.blockedCountries') ||
                  'Заблокированные страны'}
                :
              </Typography>
              <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
                {blockedCountriesCount}
              </Typography>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-white border border-tin rounded-2xl shadow-xl"
            onMouseEnter={openBlockedCountries}
            onMouseLeave={scheduleBlockedCountriesClose}
          >
            <BlockedCountriesTooltip
              countries={blockedCountries}
              offerShortId={offerShortId}
              modelUsername={offer.of_user_name}
              profileUrl={profileUrl}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <div className="flex items-center gap-1">
          <Typography variant="body3" className="text-auth-muted text-xs">
            {t('partner.revshare.offers.offerCard.blockedCountries') || 'Заблокированные страны'}:
          </Typography>
          <Typography variant="body3" className="text-auth-ink text-sm font-semibold">
            {blockedCountriesCount}
          </Typography>
        </div>
      )}

      {/* Working hours */}
      {workingHours && (
        <ResponsiveTooltip
          content={
            offer.offer?.chat_processing === 'all time'
              ? t('partner.revshare.offers.offerCard.allTimeChatsTooltip') ||
                'Этот аккаунт доступен к чату 24 часа в сутки, 7 дней в неделю'
              : offer.offer?.chat_processing === 'custom work time'
                ? t('partner.revshare.offers.offerCard.customWorkTimeTooltip') ||
                  'Аккаунт работает по расписанию'
                : t('partner.revshare.offers.offerCard.aiChatsTooltip') ||
                  'Чат обрабатывается искусственным интеллектом'
          }
        >
          <Typography
            variant="body3"
            className="text-auth-ink text-sm font-semibold cursor-pointer"
          >
            {workingHours}
          </Typography>
        </ResponsiveTooltip>
      )}
    </div>
  );
};

export default OfferDetailsCell;
